import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getTagTree } from "src/docuclipper/api";
import { ReduxState } from "..";

interface TagNode {
  id: string;
  name: string;
  metadata: { [key: string]: string };
  children?: { [key: string]: TagNode };
}

interface TagLevels {
  root: TagNode | null;
}

const initialState: {
  tagLevels: TagLevels;
  selectedInvoiceId: string | null;
  selectedL1: string | null;
  selectedL2: string | null;
  loading: boolean;
  error: string | null;
  lastSelectedL1: string | null;
  lastSelectedL2: string | null;
} = {
  tagLevels: {
    root: null
  },
  selectedInvoiceId: null,
  selectedL1: null,
  selectedL2: null,
  loading: false,
  error: null,
  lastSelectedL1: localStorage.getItem('lastSelectedL1'),
  lastSelectedL2: localStorage.getItem('lastSelectedL2')
};

const slice = createSlice({
  name: "TagLevel",
  initialState,
  reducers: {
    setTagTree(state, action: PayloadAction<TagNode>) {
      state.tagLevels.root = action.payload;
    },
    setSelectedInvoiceId(state, action: PayloadAction<string | null>) {
      state.selectedInvoiceId = action.payload;
    },
    setSelectedL1(state, action: PayloadAction<string | null>) {
      state.selectedL1 = action.payload;
    },
    setSelectedL2(state, action: PayloadAction<string | null>) {
      state.selectedL2 = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setLastSelected(state, action: PayloadAction<{ l1: string | null; l2: string | null }>) {
      const { l1, l2 } = action.payload;

      state.lastSelectedL1 = l1;
      if (l1) {
        localStorage.setItem('lastSelectedL1', l1);
      } else {
        localStorage.removeItem('lastSelectedL1');
      }

      state.lastSelectedL2 = l2;
      if (l2) {
        localStorage.setItem('lastSelectedL2', l2);
      } else {
        localStorage.removeItem('lastSelectedL2');
      }
    }
  },
});

export const {
  setTagTree,
  setSelectedInvoiceId,
  setSelectedL1,
  setSelectedL2,
  setLoading,
  setError,
  setLastSelected
} = slice.actions;

export const fetchTagLevels = () => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(null));

    const tagTree = await getTagTree();
    dispatch(setTagTree(tagTree));
  } catch (err) {
    dispatch(setError(err instanceof Error ? err.message : 'Failed to fetch tag tree'));
  } finally {
    dispatch(setLoading(false));
  }
};

export default slice.reducer;
